import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ISuppliers } from '@/modules/workforce/Crew/types';
import { Avatar, Button, Typography } from '@/shared/components/atoms';
import Badges from '@/shared/components/atoms/Badges';
import QrCodeModal from '@/shared/components/molecules/QrCodeModal.tsx';
import { useAuth } from '@/shared/hooks/context/auth';
import { getLocalizedDate } from '@/shared/lib/dates';

import Arrow from '~/images/shared/arrow.svg';
import CrewIcon from '~/images/shared/new-single-person.svg';
import QrCodeIcon from '~/images/shared/scan-barcode.svg';
import SkillsIcon from '~/images/shared/skilsIcon.svg';

interface IProps {
  basicInfo?: ISuppliers.IBasicInfo;
  qrCode?: string;
  skills?: ISuppliers.ISkillsItem[];
  crew?: ISuppliers.ICrew;
}
export function HeaderCards({ basicInfo, crew, qrCode, skills }: IProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [showQRCode, setShowQRCode] = useState(false);
  /* ************************************* functions ************************************ */
  const handleOpenQrCode = () => {
    setShowQRCode(true);
  };
  const closeQrModalHandler = () => {
    setShowQRCode(false);
  };

  /* ************************************* render ************************************ */

  return (
    <div className='grid  gap-16 tablet:grid-cols-6'>
      {/* *************** worker info ********* */}
      <div className='col-span-3 flex items-center  gap-16 rounded-xl bg-white p-16'>
        <Avatar
          size='2xl'
          variant={basicInfo?.profilePicture ? undefined : 'single'}
          alt={basicInfo?.workerName}
          src={basicInfo?.profilePicture}
        />
        <div>
          <div className='flex items-center gap-2'>
            <Typography variant='h2' weight='medium'>
              {basicInfo?.workerName}
            </Typography>
            {basicInfo?.status === 'INACTIVE' && (
              <span className='h-fit rounded-lg bg-error-90 px-16 py-1 text-14 font-semi-bold text-error-20'>
                {t('inactive')}
              </span>
            )}
          </div>
          <Typography
            variant='body1'
            className='inline-flex flex-wrap gap-4 capitalize'
          >
            <span className='text-surface-10'>{t('registered-by')} :</span>{' '}
            {basicInfo?.registeredBy} -
            {basicInfo?.registrationDate
              ? getLocalizedDate({ date: basicInfo?.registrationDate })
              : ''}
          </Typography>
        </div>
      </div>
      {/* *************** crew info ********* */}
      <div className='flex flex-col items-center justify-center gap-8 rounded-xl bg-white p-16'>
        <CrewIcon className='text-40' />
        <Typography variant='subtitle2'>{t('crew')}</Typography>
        <Typography variant='subtitle2' weight='semiBold'>
          {crew?.supplierName}
        </Typography>
      </div>
      {/* *************** skils info ********* */}
      <div className='flex flex-col items-center justify-center  gap-8 rounded-xl bg-white p-16'>
        <SkillsIcon className='text-40' />
        <Typography variant='subtitle2'>{t('skills')}</Typography>
        <Badges
          badgesList={skills || []}
          getLabel={(skill) => skill?.jobName || ''}
          show={1}
          variant='text'
          customLabel={(label) => (
            <Typography variant='subtitle2' weight='semiBold'>
              {label}
            </Typography>
          )}
        />
      </div>
      {/* *************** qrcode  ********* */}
      <div className='flex flex-col items-center justify-center gap-8 rounded-xl bg-white p-16'>
        <QrCodeIcon className='text-40' />
        <Typography variant='subtitle2'>{t('qrCode')}</Typography>
        <Button
          onClick={handleOpenQrCode}
          size='full'
          variant='text'
          className='px-rounded-xl  flex items-center justify-center gap-8 bg-surface-0 py-4  '
        >
          <>
            {t('show')}
            <span className='ltr:rotate-180'>
              <Arrow />
            </span>
          </>
        </Button>
      </div>
      <QrCodeModal
        qrData={{
          qrCode: qrCode,
          logo: user?.companyLogo,
          name: basicInfo?.workerName,
          title: t('worker'),
        }}
        open={showQRCode}
        closeModal={closeQrModalHandler}
      />
    </div>
  );
}
